import { BrandMark } from '@/components/layout/Brand';
import { cn } from '@/lib/cn';

/**
 * Waiting state that looks like the product it belongs to.
 *
 * Used where a skeleton would be dishonest — when there is no shape to
 * anticipate yet, such as the first paint of a lazily loaded screen. Once the
 * layout is known, a skeleton communicates more, so prefer those.
 *
 * The mark pulses rather than spins: a spinner suggests a measurable wait, and
 * these are usually over in a few hundred milliseconds.
 */

export interface BrandLoaderProps {
  /** Announced to screen readers and shown under the mark. */
  label?: string;
  /** `page` fills the viewport; `inline` sits inside a card or a panel. */
  variant?: 'page' | 'inline';
  className?: string;
}

export function BrandLoader({
  label = 'Cargando…',
  variant = 'page',
  className,
}: BrandLoaderProps): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center',
        variant === 'page' ? 'min-h-[60vh] p-6' : 'p-6',
        className,
      )}
    >
      <BrandMark
        className={cn(
          variant === 'page' ? 'h-12 w-12' : 'h-9 w-9',
          // Held still for anyone who asked the system for less motion.
          'motion-safe:animate-brand-pulse',
        )}
      />
      <span className="text-caption text-ink-muted">{label}</span>
    </div>
  );
}
